/**
 * FP3-033 — Student Progress Summary (Display).
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import {
  DisplayShell,
  PrintAccessGate,
  classifyError,
} from "@/components/print/PrintShell";

function fmtPct(v) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return `${n.toFixed(0)}%`;
}

export default function DisplayStudentProgressSummary() {
  const { studentId } = useParams();
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState("");
  const [student, setStudent] = useState(null);
  const [docs, setDocs] = useState([]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.getStudent(studentId),
      api.listStudentDocuments(studentId).catch(() => ({ data: { items: [] } })),
    ])
      .then(([s, d]) => {
        if (cancelled) return;
        setStudent(s.data);
        setDocs((d.data?.items || []).filter((x) => x.status !== "archived").slice(0, 4));
        setPhase("ready");
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.detail || err.message);
        setPhase(classifyError(err));
      });
    return () => { cancelled = true; };
  }, [studentId]);

  const name = student ? `${student.first_name} ${student.last_name}` : "Student";
  const provider = student?.provider_details?.[0];
  const goals = (student?.goals || []).filter(
    (g) => (g.status || "active") === "active"
  );
  const sessions = (student?.sessions || []).slice(0, 5);

  return (
    <DisplayShell
      title="Progress Summary"
      subtitle={`${name}${student?.school_name ? ` · ${student.school_name}` : ""}`}
      testId="display-progress-summary"
    >
      <PrintAccessGate phase={phase} error={error}>
        {student && (
          <>
            <div className="fp-display-card" data-testid="display-progress-header">
              <div className="fp-display-grid-3">
                <div>
                  <div className="fp-display-label">Student</div>
                  <div className="fp-display-value">{name}</div>
                </div>
                <div>
                  <div className="fp-display-label">Grade · School</div>
                  <div className="fp-display-value">
                    {[student.grade && `Grade ${student.grade}`, student.school_name]
                      .filter(Boolean).join(" · ") || "—"}
                  </div>
                </div>
                <div>
                  <div className="fp-display-label">Provider</div>
                  <div className="fp-display-value">
                    {provider
                      ? `${provider.first_name || ""} ${provider.last_name || ""}`.trim()
                      : "—"}
                  </div>
                </div>
              </div>
            </div>

            <div className="fp-display-card">
              <h2>Active goals · {goals.length}</h2>
              {goals.length === 0 ? (
                <p style={{ margin: 0 }}>No active goals.</p>
              ) : (
                goals.map((g) => (
                  <div
                    key={g.id}
                    data-testid={`display-progress-goal-${g.id}`}
                    style={{
                      padding: "14px 0",
                      borderTop: "1px solid var(--fp-print-line)",
                    }}
                  >
                    <p style={{ fontSize: 17, margin: "0 0 6px" }}>{g.goal_text}</p>
                    <div className="fp-display-grid-3">
                      <div>
                        <div className="fp-display-label">Baseline</div>
                        <div className="fp-display-value">{fmtPct(g.baseline_mastery)}</div>
                      </div>
                      <div>
                        <div className="fp-display-label">Target</div>
                        <div className="fp-display-value">{fmtPct(g.target_mastery)}</div>
                      </div>
                      <div>
                        <div className="fp-display-label">Current</div>
                        <div className="fp-display-value">{fmtPct(g.current_mastery)}</div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="fp-display-card" data-testid="display-progress-sessions">
              <h2>Recent activity</h2>
              {sessions.length === 0 ? (
                <p style={{ margin: 0 }}>No sessions on file.</p>
              ) : (
                <ul style={{ paddingLeft: 0, listStyle: "none", margin: 0 }}>
                  {sessions.map((s) => (
                    <li
                      key={s.id}
                      style={{
                        padding: "10px 0",
                        borderTop: "1px solid var(--fp-print-line)",
                        fontSize: 16,
                      }}
                    >
                      <strong>{s.date || "—"}</strong>{" "}
                      · {s.session_type || s.kind || "session"}{" "}
                      · {s.minutes_actual ?? s.minutes_scheduled ?? "—"} min{" "}
                      · {s.attendance || "—"}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {docs.length > 0 && (
              <div className="fp-display-card" data-testid="display-progress-documents">
                <h2>Recent documents</h2>
                <ul style={{ paddingLeft: 0, listStyle: "none", margin: 0 }}>
                  {docs.map((d) => (
                    <li
                      key={d.id}
                      style={{
                        padding: "8px 0",
                        borderTop: "1px solid var(--fp-print-line)",
                        fontSize: 15,
                      }}
                    >
                      <strong>{d.document_title}</strong>{" "}
                      · {d.document_type}{" "}
                      · {d.document_date || "—"}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </PrintAccessGate>
    </DisplayShell>
  );
}
